import { isEmptyArray } from "formik";
import * as React from "react";
import { RouteProps } from "react-router-dom";
import { HomePageAcf } from "../../constants/types/acf";
import {
    useAppState,
    useChallengingSituations,
    useConditions,
    useCurrentRouteData,
    useGlobalAcf,
    usePopulations,
} from "../../services/app-state";
import { useSuburbName,useSuburbInformationNullable } from "../../utils";
import { colors } from "../../utils/colors";
import { Separator } from "../atoms/Separator";
import { RelatedLocalCta } from "../molecules/RegionSelectCta";
import { ConditionList } from "../organisms/ConditionList";
import { ErrorBoundary } from "../organisms/ErrorBoundary";
import { FeaturedServices } from "../organisms/FeaturedServices";
import { HeroHome } from "../organisms/HeroHome";
import { HomeChallengingSituations } from "../organisms/HomeChallengingSituations";
import { OtherSupport } from "../organisms/HomeOtherSupport";
import { LocalInformationV2 } from "../organisms/LocalInformationV2";
import { NotSureWhereToBegin } from "../organisms/NotSureWhereToBegin";
import { PopulationList } from "../organisms/PopulationList";
import { PullUpSection } from "../organisms/PullUpSection";

const HomeInner: React.FC<RouteProps> = () => {
    const page = useCurrentRouteData<HomePageAcf>();
    const [populations] = usePopulations();
    const [conditions] = useConditions();
    const [situations] = useChallengingSituations();
    const [
        {
            custom_page_titles,
        },
    ] = useGlobalAcf();
    const suburbName = useSuburbName();
    const suburbdetail = useSuburbInformationNullable()?.regionname;

    const [{ featuredServices }] = useAppState();
    const [{ localInformation }] = useAppState();
    
    let enabledfeaturedServices : boolean | undefined = false;
    if(isEmptyArray(featuredServices)) {
        enabledfeaturedServices = false;
    } else {
        enabledfeaturedServices = true;
    }

    let enabledlocalInformation : boolean | undefined = false;
    if(isEmptyArray(localInformation)) {
        enabledlocalInformation = false;
    } else {
        enabledlocalInformation = true;
    }

    return (
        <ErrorBoundary>
            <HeroHome
                regionName={suburbdetail}
                suburbName={suburbName}
                bannerImage={page.acf.homepage_hero_section.hero_banner_image}
                mobileBannerImage={page.acf.homepage_hero_section.hero_banner_mobile_image}
            />
            <NotSureWhereToBegin topColor={"transparent"} bottomColor={"white"} />

            <Separator />
            <PopulationList
                title={custom_page_titles.populations}
                description={page.acf.populations_section.copy}
                items={populations}
            />

            <Separator />
            <ConditionList
                id="disorders-concerns"
                title={custom_page_titles.conditions}
                conditions={conditions}
            />

            <Separator />
            <HomeChallengingSituations
                id="challenging-situations"
                title={custom_page_titles.challenging_situations}
                conditions={situations}
            />

            {/* Featured service section */}
            { enabledfeaturedServices ? (
                <>
                    <Separator />
                    <FeaturedServices
                        items={featuredServices}
                        id="featured-services"
                        title="Featured services"
                    />
                </>
            ) : null
            }

            {/* Display suburb selection if user has not sleected it already */}
            { !suburbName ? (
                <section style={{marginBottom: "40px"}}>
                    <RelatedLocalCta />
                </section>
            ) : null
            }

            {/* Local information section */}
            { enabledlocalInformation ? (
                <>
                    <Separator />
                    <LocalInformationV2
                        items={localInformation}
                        id="local-information"
                        title="Local information"
                        isCarousel={false}
                    />
                </>
            ) : null}

            <div style={{ backgroundColor: "white" }}>
                <OtherSupport />
            </div>

            <PullUpSection
                topColor={colors.brandMid}
                bottomColor={colors.white}
                image={page.acf.for_health_professionals.image}
                title={page.acf.for_health_professionals.title}
                description={page.acf.for_health_professionals.copy}
                button={page.acf.for_health_professionals.button}
            />
        </ErrorBoundary>
    );
};

export const Home: React.FC<RouteProps> = props => (
    <ErrorBoundary>
        <HomeInner {...props} />
    </ErrorBoundary>
);

export default Home;
